import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { ShieldCheck, Sparkles, Zap, Save, RotateCcw, Crown, Check, X, Mail } from 'lucide-react';

const VERDICT_STYLE = {
  approved: 'bg-green-500/15 text-green-600',
  needs_human_review: 'bg-amber-500/15 text-amber-600',
  rejected: 'bg-red-500/15 text-red-600',
};

const XPControlPanel = ({ members = [] }) => {
  const { api, user } = useAuth();
  const [rules, setRules] = useState({});
  const [alerts, setAlerts] = useState({});
  const [claims, setClaims] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [saving, setSaving] = useState(false);
  const [decision, setDecision] = useState({ open: false, claim: null, approve: true, note: '' });
  const [adjust, setAdjust] = useState({ user_id: '', delta: '', points: '', reason: 'Ajustement manuel' });

  const load = useCallback(async () => {
    try {
      const [settingsRes, claimsRes, ledgerRes] = await Promise.all([
        api.get('/admin/xp/settings'),
        api.get('/admin/xp/milestone-claims'),
        api.get('/admin/xp/ledger?limit=40'),
      ]);
      setRules(settingsRes.data.rules || {});
      setAlerts(settingsRes.data.admin_alerts || {});
      setClaims(claimsRes.data || []);
      setLedger(ledgerRes.data || []);
    } catch (e) {
      toast.error("Erreur de chargement du panneau XP");
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const pendingClaims = useMemo(
    () => claims.filter(c => c.status === 'pending_coach' || c.status === 'pending_ai'),
    [claims]
  );

  const updateRule = (source, field, value) => {
    setRules(prev => ({
      ...prev,
      [source]: { ...prev[source], [field]: value === '' ? null : Number(value) },
    }));
  };

  const saveAlerts = async (next) => {
    setAlerts(next);
    try {
      await api.put('/admin/xp/settings', { admin_alerts: next });
      toast.success('Alertes e-mail mises à jour');
    } catch (e) {
      toast.error('Erreur');
      load();
    }
  };

  const saveRules = async () => {
    setSaving(true);
    try {
      await api.put('/admin/xp/settings', { rules });
      toast.success('Règles XP enregistrées');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const submitDecision = async () => {
    try {
      const res = await api.post(`/admin/xp/milestone-claims/${decision.claim.id}/decide`, {
        approve: decision.approve,
        admin_note: decision.note,
      });
      toast.success(res.data.message);
      setDecision({ open: false, claim: null, approve: true, note: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const submitAdjust = async () => {
    if (!adjust.user_id) return toast.error('Choisis un membre');
    try {
      const payload = { user_id: adjust.user_id, reason: adjust.reason };
      if (adjust.points !== '') payload.points = parseInt(adjust.points, 10);
      else if (adjust.delta !== '') payload.delta = parseInt(adjust.delta, 10);
      else return toast.error('Renseigne un delta ou un total XP');
      const res = await api.post('/admin/xp/adjust', payload);
      toast.success(`${res.data.message} — niveau ${res.data.level}`);
      setAdjust({ user_id: adjust.user_id, delta: '', points: '', reason: 'Ajustement manuel' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const maxOut = async (targetId) => {
    try {
      const res = await api.post('/admin/xp/max-out', { user_id: targetId, reason: 'Passage niveau max (admin)' });
      toast.success(`${res.data.message} — niveau ${res.data.level}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="space-y-6" data-testid="xp-control-panel">
      {/* Milestone claims queue */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading text-lg">
            <ShieldCheck className="w-5 h-5 text-primary" />
            Jalons, objectifs & défis à valider ({pendingClaims.length})
          </CardTitle>
          <CardDescription>
            Déclarations hors plateforme pré-analysées par l'IA. Aucune XP n'est versée avant ta validation.
            Les déclarations jugées plausibles par l'IA sont créditées automatiquement et n'apparaissent pas ici.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pendingClaims.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune déclaration en attente.</p>
          ) : (
            <div className="space-y-3">
              {pendingClaims.map((claim) => (
                <div key={claim.id} className="p-3 rounded-lg border border-border" data-testid={`claim-${claim.id}`}>
                  <div className="flex flex-wrap items-center gap-2 justify-between">
                    <div className="min-w-0">
                      <div className="font-medium text-sm flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {claim.kind === 'objective' ? 'Objectif' : claim.kind === 'challenge' ? 'Défi' : 'Jalon'}
                        </Badge>
                        {claim.milestone_title}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {claim.user_name} · {claim.user_email} · +{claim.xp_reward} XP
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`text-xs gap-1 ${VERDICT_STYLE[claim.ai_verdict?.verdict] || ''}`}>
                        <Sparkles className="w-3 h-3" />
                        IA : {claim.ai_verdict?.verdict === 'approved' ? 'plausible'
                          : claim.ai_verdict?.verdict === 'rejected' ? 'douteux' : 'à vérifier'}
                        {typeof claim.ai_verdict?.confidence === 'number' ? ` ${claim.ai_verdict.confidence}%` : ''}
                      </Badge>
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => setDecision({ open: true, claim, approve: true, note: '' })}
                        data-testid={`approve-claim-${claim.id}`}
                      >
                        <Check className="w-4 h-4 mr-1" />Valider
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-500/50 text-red-500"
                        onClick={() => setDecision({ open: true, claim, approve: false, note: '' })}
                        data-testid={`reject-claim-${claim.id}`}
                      >
                        <X className="w-4 h-4 mr-1" />Refuser
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs mt-2 whitespace-pre-wrap bg-secondary/40 rounded p-2">{claim.proof_text}</p>
                  {claim.ai_verdict?.reason && (
                    <p className="text-xs text-muted-foreground mt-1.5">
                      <span className="font-medium">Analyse IA :</span> {claim.ai_verdict.reason}
                    </p>
                  )}
                  {claim.ai_verdict?.red_flags?.length > 0 && (
                    <p className="text-xs text-red-500 mt-1">Signaux : {claim.ai_verdict.red_flags.join(' · ')}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual XP adjustment */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading text-lg">
            <Zap className="w-5 h-5 text-primary" />
            Ajuster l'XP d'un membre
          </CardTitle>
          <CardDescription>Delta signé (+/-) ou total XP absolu. Le niveau, le titre et le cadre sont recalculés.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 md:grid-cols-4">
            <div className="space-y-1 md:col-span-2">
              <Label className="text-xs">Membre</Label>
              <Select value={adjust.user_id} onValueChange={(v) => setAdjust({ ...adjust, user_id: v })}>
                <SelectTrigger data-testid="xp-adjust-member">
                  <SelectValue placeholder="— Choisir un membre —" />
                </SelectTrigger>
                <SelectContent>
                  {members.map(m => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.first_name} {m.last_name} — {m.points || 0} XP (niv. {m.level || 1})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Delta XP (+/-)</Label>
              <Input
                type="number"
                value={adjust.delta}
                onChange={(e) => setAdjust({ ...adjust, delta: e.target.value, points: '' })}
                placeholder="ex : -500"
                data-testid="xp-adjust-delta"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">ou total XP</Label>
              <Input
                type="number"
                value={adjust.points}
                onChange={(e) => setAdjust({ ...adjust, points: e.target.value, delta: '' })}
                placeholder="ex : 4700"
                data-testid="xp-adjust-points"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Motif (journalisé)</Label>
            <Input
              value={adjust.reason}
              onChange={(e) => setAdjust({ ...adjust, reason: e.target.value })}
              data-testid="xp-adjust-reason"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={submitAdjust} data-testid="xp-adjust-submit">
              <Save className="w-4 h-4 mr-2" />Appliquer
            </Button>
            <Button
              variant="outline"
              onClick={() => maxOut(adjust.user_id || user?.id)}
              data-testid="xp-maxout-btn"
            >
              <Crown className="w-4 h-4 mr-2" />
              Passer au niveau max {adjust.user_id ? '(membre sélectionné)' : '(moi)'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* E-mail alerts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading text-lg">
            <Mail className="w-5 h-5 text-primary" />
            Alertes e-mail
          </CardTitle>
          <CardDescription>
            Tu reçois un e-mail (et une notification dans l'app) à chaque déclaration de jalon, d'objectif ou de défi.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {[
            { key: 'on_claim', label: 'Déclaration en attente de ma validation', hint: 'Recommandé : aucune XP versée avant ta décision' },
            { key: 'on_auto_approved', label: 'Déclaration validée automatiquement par l\'IA', hint: 'Pour garder un œil sur les XP créditées sans toi' },
            { key: 'on_auto_rejected', label: 'Déclaration refusée automatiquement par l\'IA', hint: 'Utile si tu veux repêcher un membre injustement refusé' },
          ].map(({ key, label, hint }) => (
            <label
              key={key}
              className="flex items-start gap-3 p-3 rounded-lg border border-border cursor-pointer hover:border-primary/40 transition-colors"
              data-testid={`alert-toggle-${key}`}
            >
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 accent-current"
                checked={!!alerts[key]}
                onChange={(e) => saveAlerts({ ...alerts, [key]: e.target.checked })}
              />
              <span>
                <span className="block text-sm font-medium">{label}</span>
                <span className="block text-xs text-muted-foreground">{hint}</span>
              </span>
            </label>
          ))}
        </CardContent>
      </Card>

      {/* Caps / rules */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading text-lg">
            <RotateCcw className="w-5 h-5 text-primary" />
            Plafonds & montants XP
          </CardTitle>
          <CardDescription>
            Montant par action, plafond quotidien, longueur minimale et délai anti-spam. Laisser vide = illimité.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Source</TableHead>
                <TableHead className="w-28">Montant</TableHead>
                <TableHead className="w-28">Plafond/jour</TableHead>
                <TableHead className="w-28">Min. caractères</TableHead>
                <TableHead className="w-28">Cooldown (s)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(rules).map(([source, rule]) => (
                <TableRow key={source} data-testid={`xp-rule-${source}`}>
                  <TableCell className="text-sm">
                    {rule.label || source}
                    {rule.one_time && <span className="ml-2 text-xs text-muted-foreground">(1 seule fois)</span>}
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      className="h-8"
                      value={rule.amount ?? ''}
                      placeholder="variable"
                      onChange={(e) => updateRule(source, 'amount', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      className="h-8"
                      value={rule.daily_cap ?? ''}
                      placeholder="illimité"
                      onChange={(e) => updateRule(source, 'daily_cap', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      className="h-8"
                      value={rule.min_chars ?? ''}
                      placeholder="—"
                      onChange={(e) => updateRule(source, 'min_chars', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      className="h-8"
                      value={rule.cooldown_sec ?? ''}
                      placeholder="—"
                      onChange={(e) => updateRule(source, 'cooldown_sec', e.target.value)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Button className="mt-4" onClick={saveRules} disabled={saving} data-testid="xp-rules-save">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Enregistrement...' : 'Enregistrer les règles'}
          </Button>
        </CardContent>
      </Card>

      {/* Ledger audit */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Journal XP global (40 derniers)</CardTitle>
          <CardDescription>Chaque XP versée ou retirée est traçable.</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Membre</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="text-right w-20">XP</TableHead>
                <TableHead className="w-40">Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ledger.map((entry) => (
                <TableRow key={entry.id} data-testid={`ledger-${entry.id}`}>
                  <TableCell className="text-sm">{entry.user_name}</TableCell>
                  <TableCell className="text-xs">{entry.label || entry.source}</TableCell>
                  <TableCell className={`text-right font-bold text-sm ${entry.amount >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {entry.amount >= 0 ? '+' : ''}{entry.amount}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(entry.created_at).toLocaleString('fr-FR')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Decision dialog */}
      <Dialog open={decision.open} onOpenChange={(open) => setDecision({ ...decision, open })}>
        <DialogContent data-testid="claim-decision-dialog">
          <DialogHeader>
            <DialogTitle>
              {decision.approve ? 'Valider' : 'Refuser'}{' '}
              {decision.claim?.kind === 'objective' ? "l'objectif" : decision.claim?.kind === 'challenge' ? 'le défi' : 'le jalon'}
            </DialogTitle>
            <DialogDescription>
              {decision.claim?.milestone_title} — {decision.claim?.user_name}
              {decision.approve ? ` · +${decision.claim?.xp_reward} XP seront créditées.` : ''}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={3}
            value={decision.note}
            onChange={(e) => setDecision({ ...decision, note: e.target.value })}
            placeholder={decision.approve ? 'Note interne (optionnel)' : 'Explique au membre ce qui manque'}
            data-testid="claim-decision-note"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecision({ open: false, claim: null, approve: true, note: '' })}>
              Annuler
            </Button>
            <Button onClick={submitDecision} data-testid="claim-decision-submit">Confirmer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default XPControlPanel;
